import type { QueryClient } from "@tanstack/react-query";
import { withTimeout } from "@/lib/with-timeout";
import {
  ensureProblemCacheFormatVersion,
  getLastSyncTimestamp,
  invalidateProblems,
  setLastSyncTimestamp,
} from "@/lib/problemDb";
import { getProblemsUpdatedSince } from "@/lib/server-actions/problemCacheActions";

const RETRY_DELAYS_MS = [150, 450] as const;

type ProblemQueryClient = Pick<QueryClient, "removeQueries">;

function isPersistentCacheQuery(meta: Record<string, unknown> | undefined) {
  return meta?.problemCacheMode !== "bypass";
}

export interface ProblemCacheSyncDependencies {
  ensureProblemCacheFormatVersion: typeof ensureProblemCacheFormatVersion;
  getLastSyncTimestamp: typeof getLastSyncTimestamp;
  setLastSyncTimestamp: typeof setLastSyncTimestamp;
  invalidateProblems: typeof invalidateProblems;
  getProblemsUpdatedSince: typeof getProblemsUpdatedSince;
  wait: (ms: number) => Promise<void>;
}

const defaultDependencies: ProblemCacheSyncDependencies = {
  ensureProblemCacheFormatVersion,
  getLastSyncTimestamp,
  setLastSyncTimestamp,
  invalidateProblems,
  getProblemsUpdatedSince,
  wait: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
};

async function retryOnError<T>(
  operation: () => Promise<T>,
  wait: ProblemCacheSyncDependencies["wait"],
  signal: AbortSignal,
): Promise<T> {
  let lastError: unknown;

  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    signal.throwIfAborted();
    try {
      const result = await operation();
      signal.throwIfAborted();
      return result;
    } catch (error) {
      signal.throwIfAborted();
      lastError = error;
    }

    const delay = RETRY_DELAYS_MS[attempt];
    if (delay !== undefined) {
      await wait(delay);
    }
  }

  throw lastError;
}

/**
 * 앱을 새로 연 시점에 문제 변경 메타를 한 번 확인하고 캐시를 동기화한다.
 * 정상 경로의 서버 호출은 1회이며, 메타 조회나 IndexedDB 삭제가 실패한 경우에만
 * 최대 2회 더 재시도한다. 전체 대기는 5초로 제한하고, 늦은 응답의 후속 변경을 중단한다.
 * 캐시 삭제 성공 전에는 마지막 동기화 시각을 전진시키지 않는다.
 */
export async function synchronizeProblemCache(
  queryClient: ProblemQueryClient,
  dependencies: ProblemCacheSyncDependencies = defaultDependencies
): Promise<void> {
  return withTimeout(async (signal) => {
    const didClearCache = await dependencies.ensureProblemCacheFormatVersion();
    signal.throwIfAborted();
    if (didClearCache) {
      // 이미 서버에서 최신 데이터를 가져오는 bypass query는 포맷 초기화 대상이 아니다.
      // 이를 제거하면 뒤이은 메타 동기화 실패 시 observer가 취소된 채 남을 수 있다.
      queryClient.removeQueries({
        queryKey: ["problem"],
        predicate: ({ meta }) => isPersistentCacheQuery(meta),
      });
      queryClient.removeQueries({
        queryKey: ["problems"],
        predicate: ({ meta }) => isPersistentCacheQuery(meta),
      });
    }

    const lastLocal = await dependencies.getLastSyncTimestamp();
    signal.throwIfAborted();
    const { updatedAt, updatedProblemIds } = await retryOnError(
      () => dependencies.getProblemsUpdatedSince(lastLocal),
      dependencies.wait,
      signal,
    );

    if (updatedProblemIds.length > 0) {
      await retryOnError(
        async () => {
          const invalidated = await dependencies.invalidateProblems(
            updatedProblemIds
          );
          if (!invalidated) {
            throw new Error("문제 캐시 무효화에 실패했습니다.");
          }
        },
        dependencies.wait,
        signal,
      );

      const updatedIds = new Set(updatedProblemIds);
      queryClient.removeQueries({
        predicate: ({ queryKey, meta }) =>
          isPersistentCacheQuery(meta) &&
          queryKey[0] === "problem" &&
          typeof queryKey[1] === "string" &&
          updatedIds.has(queryKey[1]),
      });
      queryClient.removeQueries({
        queryKey: ["problems"],
        predicate: ({ meta }) => isPersistentCacheQuery(meta),
      });
    }

    if (updatedAt > lastLocal) {
      await dependencies.setLastSyncTimestamp(updatedAt);
    }
  }, 5000, "문제 캐시 동기화 시간이 초과되었습니다.");
}
